import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CollapsibleSidebar} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {CollapsibleFeedbackLink} from '@github-ui/repos-issues-sidebar/CollapsibleFeedbackLink'
import {SIDEBAR_STORAGE_KEY} from '@github-ui/repos-issues-sidebar/constants'
import type {CollapsibleLeftPaneConfig, PaneWithAriaLabel} from '@github-ui/three-panes-layout'
import type {ReactNode} from 'react'

const SIDEBAR_ARIA_LABEL = 'Issues sidebar navigation'

/**
 * Shared sidebar configuration for every page that renders the issues sidebar
 * inside a `ThreePanesLayout`.
 *
 * Flag matrix:
 * - `issue_pinned_views` OFF → no sidebar (returns {}). The existing pages
 *   render without a left pane.
 * - `issue_pinned_views` ON + `issues_react_collapsible_sidebar` OFF → render
 *   the sidebar in a fixed non-collapsible left pane (legacy behaviour).
 * - `issue_pinned_views` ON + `issues_react_collapsible_sidebar` ON → render
 *   the sidebar inside a `CollapsibleSidebar` with a persisted toggle so the
 *   collapse state is shared across the issues, milestones, labels and
 *   views pages.
 */
export function useRepoIssuesSidebarPanes(renderSidebar: (collapsible?: boolean) => ReactNode): {
  collapsibleLeftPane?: CollapsibleLeftPaneConfig
  leftPane?: PaneWithAriaLabel
} {
  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- issue_pinned_views uses actor-aware server-side evaluation via PinnedViewsFeature; isFeatureEnabled only knows current_user.
  if (!useFeatureFlag('issue_pinned_views')) {
    return {}
  }

  if (isFeatureEnabled('issues_react_collapsible_sidebar')) {
    return {
      collapsibleLeftPane: {
        storageKey: SIDEBAR_STORAGE_KEY,
        expandedWidth: 'small',
        render: () => <>{renderSidebar(true)}</>,
        renderFooter: ({isExpanded, toggle}) => (
          <>
            <CollapsibleFeedbackLink isExpanded={isExpanded} />
            <CollapsibleSidebar.Toggle isExpanded={isExpanded} onToggle={toggle} />
          </>
        ),
      },
    }
  }

  return {
    leftPane: {
      element: <>{renderSidebar(false)}</>,
      ariaLabel: SIDEBAR_ARIA_LABEL,
    },
  }
}
